/**
 * ParentContextBar - Phase 4.7A Hierarchical Cohesion
 * 
 * Displays read-only parent hierarchy: Client → Advertiser → Booking → [Campaign]
 * Each segment is clickable for navigation.
 */
import React from "react";
import { Link } from "wouter";
import { ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

export interface ParentContextData {
    clientId?: number | null;
    clientName?: string | null;
    advertiserId?: number | null;
    advertiserName?: string | null;
    bookingId?: number | null;
    bookingName?: string | null;
    campaignName: string;
}

interface ParentContextBarProps {
    data: ParentContextData;
    className?: string;
}

function ContextSegment({
    label,
    href,
    isLast = false,
}: {
    label: string;
    href?: string;
    isLast?: boolean;
}) {
    const content = href ? (
        <Link href={href}>
            <span className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer transition-colors">
                {label}
            </span>
        </Link>
    ) : (
        <span className="text-muted-foreground">{label}</span>
    );

    return (
        <span className="flex items-center gap-1.5">
            {content}
            {!isLast && (
                <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/60 flex-shrink-0" />
            )}
        </span>
    );
}

export function ParentContextBar({ data, className }: ParentContextBarProps) {
    const {
        clientId,
        clientName,
        advertiserId,
        advertiserName,
        bookingId,
        bookingName,
        campaignName,
    } = data;

    // Account = primary commercial owner (advertiser with optional client context)
    const hasAccount = !!advertiserName;
    const hasBooking = !!bookingId;

    // Determine display values
    const bookingDisplay = bookingId
        ? bookingName || `Booking #${bookingId}`
        : "Manual Campaign";

    // Determine hrefs - Account links to unified account page
    const accountHref = advertiserId ? `/accounts/${advertiserId}` : undefined;
    const bookingHref = bookingId ? `/admin/bookings/${bookingId}` : undefined;

    // Build segments array - Account → Booking → Campaign
    const segments: { label: string; href?: string }[] = [];
    if (hasAccount) {
        segments.push({ label: advertiserName!, href: accountHref });
    }
    // Always show booking status (Manual Campaign or booking name)
    segments.push({ label: bookingDisplay, href: bookingHref });
    // Always show campaign name as last segment
    segments.push({ label: campaignName || "Campaign" });

    return (
        <div
            className={cn(
                "flex items-center gap-1.5 px-4 py-2 bg-slate-50 border-b border-slate-200 text-sm",
                className
            )}
        >
            {segments.map((segment, index) => (
                <ContextSegment
                    key={index}
                    label={segment.label}
                    href={segment.href}
                    isLast={index === segments.length - 1}
                />
            ))}
        </div>
    );
}

export default ParentContextBar;
